import { months } from './constants';
import type { NormalizedQuotePlan } from './quotePriceResponse';
import type { ISelectedAddon } from '../types';
import { getTotalPremiumInclGst } from './quotePriceResponse';

/** Same four fields as backend `Addon` for quote/issue (no client-side premium math). */
function toAddonPayload(a: ISelectedAddon): {
    id: string;
    name: string;
    price: number;
    gst: number;
} {
    return {
        id: a.id,
        name: a.name,
        price: Number(a.price),
        gst: Number(a.gst),
    };
}

export function mapAddonIdsForQuotePriceRequest(
    selectedAddons: ISelectedAddon[] | undefined
): string[] {
    return (selectedAddons ?? [])
        .map((a) => String(a.id ?? '').trim())
        .filter(Boolean);
}

export function mapAddonsForIssueRequestFromPlan(
    quotePlan: NormalizedQuotePlan | null | undefined,
    selectedAddons: ISelectedAddon[] | undefined
) {
    const lines = quotePlan?.addons?.filter(Boolean) ?? [];
    if (!lines.length) {
        return (selectedAddons ?? []).map(toAddonPayload);
    }
    return lines.map((line) => ({
        id: line.addonId ?? '',
        name: line.name ?? '',
        price: line.price != null ? Number(line.price) : 0,
        gst: line.gst != null ? Number(line.gst) : 0,
    }));
}

export const constructPayload = (state) => {
    const {
        name,
        phoneNumber,
        email,
        pincode,
        address,
        vehicleType,
        registrationNumber,
        registrationYear,
        registrationMonth,
        engineNumber,
        chassisNumber,
        isVehicleFinanced,
        financierName,
        isPreviousPolicyExpired,
        claimedInPreviousPolicy,
        previousPolicyInsurer,
        totalAmount,
        selectedAddons,
        quotePlan,
        previousPolicyExpiryDate,
        policyStartDate,
        make,
        variant,
        variant_id,
        numberOfTrailers,
        trailersInfo,
    } = state;

    const attached_trailers =
        vehicleType?.value === 'tractor' &&
        numberOfTrailers > 1 &&
        trailersInfo.map((t) => {
            return {
                trailer_type: t?.type?.value,
                registration_number: t?.registrationNumber,
                chassis_number: t?.chassisNumber,
            };
        });

    const totalFromPlan = getTotalPremiumInclGst(quotePlan);
    const totalAmountForPayload =
        totalFromPlan > 0
            ? totalFromPlan
            : totalAmount != null && Number.isFinite(Number(totalAmount))
            ? Math.round(Number(totalAmount))
            : 0;

    const addonLines = mapAddonsForIssueRequestFromPlan(
        quotePlan,
        selectedAddons
    );

    const paylaodData = {
        user: {
            name,
            phone: phoneNumber,
            email,
            pincode,
            communication_address: address,
        },
        vehicle: {
            mmv_details: {
                make: make.name,
                model: make.name,
                variant: variant.name,
                variant_id,
                variant_ekey: variant.id,
            },
            registration_year: registrationYear,
            registration_month: months.filter(
                (m) => m?.name === registrationMonth
            )[0].value,
            registration_number: registrationNumber,
            vehicle_type: vehicleType?.value,
            engine_number: engineNumber,
            chassis_number: chassisNumber,
            hypothecation: {
                is_financed: Boolean(parseInt(isVehicleFinanced?.value, 10)),
                ...(Boolean(parseInt(isVehicleFinanced?.value, 10)) && {
                    financier_name: financierName,
                }),
            },
            previous_policy_details: {
                is_claimed: Boolean(
                    parseInt(claimedInPreviousPolicy?.value, 10)
                ),
                is_expired: Boolean(
                    parseInt(isPreviousPolicyExpired?.value, 10)
                ),
                previous_insurer_name: previousPolicyInsurer,
                expiry_date: new Date(
                    new Date(previousPolicyExpiryDate).setHours(0, 0, 0, 0)
                ),
            },
            ...(attached_trailers && { attached_trailers }),
        },
        addons: addonLines,
        total_amount: totalAmountForPayload,
        policy_start_date: policyStartDate,
    };

    return paylaodData;
};

export const formatDate = (date: Date) => {
    return `${date.getDate()} / ${date.getMonth() + 1} / ${date.getFullYear()}`;
};

export const currentYear = new Date().getFullYear();

export const getRegistrationYears = () => {
    const years = [...Array(new Date().getFullYear() - 2000 + 1).keys()].map(
        (k) => {
            const year = (currentYear - k).toString();
            return { name: year, value: year };
        }
    );
    return years;
};
